import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import PropTypes from 'prop-types';
import TableHeader from 'components/Table/TableHeader';

const initFind = {
  search: '',
  order_by: 'tb.created_at',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

const initSummary = {
  quantity: '',
};

function QueryProductPrice({ productCode, setLoading }) {
  const mod = 'rp_product_query';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [otherBranch, setOtherBranch] = useState({});
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);

  const initHeader = [
    { id: 'branch_name', label: 'สาขา', align: 'left' },
    { id: 'price', label: 'ราคาสุทธิ', align: 'right' },
    { id: 'cost_sale', label: 'ทุนฝ่ายขาย', align: 'right' },
    { id: 'cost_with_vat', label: 'ทุนรวมภาษี', align: 'right' },
    { id: 'gross_profit_percent', label: 'กำไรขั้นต้น %', align: 'right' },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('OPTIONS', `/${mod}/product_price`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
    }).then((result) => {
      if (result.status === 'ok') {
        console.log(result);
        setRecord(result.data);
        setOtherBranch(result.other);
        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    if (productCode) view({ ...find, search: productCode, page: 1 });
  }, [productCode]);

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const renderList = () => {
    return (
      <>
        <TableContainer component={Paper} sx={{ minHeight: 400 }}>
          <Table aria-label='datatable table'>
            <TableHeader
              order={order}
              orderBy={orderBy}
              onRequestSort={handleSort}
              data={initHeader}
            />
            <TableBody>
              {record.length === 0 || !productCode ? (
                <TableRow>
                  <TableCell colSpan={initHeader.length} align='center'>
                    ไม่พบข้อมูล
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {otherBranch && (
                    <TableRow key={otherBranch.id} hover>
                      <TableCell align='left'>
                        <MDBox>สาขาอื่น ๆ</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(otherBranch.price)}</MDBox>
                        <MDBox>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(otherBranch.cost_sell)}</MDBox>
                        <MDBox>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(otherBranch.cost_sell * (1 + 7 / 100))}</MDBox>
                        <MDBox>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right' className='highlight-success'>
                        <MDBox>{Func.numberDisplay(otherBranch.gross_profit_percent)}</MDBox>
                        <MDBox>%</MDBox>
                      </TableCell>
                    </TableRow>
                  )}
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='left'>
                        <MDBox>{row.branch_name ?? 'ทุกสาขา'}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(row.price)}</MDBox>
                        <MDBox>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(row.cost_sale)}</MDBox>
                        <MDBox>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(row.cost_sale * (1 + 7 / 100))}</MDBox>
                        <MDBox>บาท</MDBox>
                      </TableCell>
                      <TableCell align='right' className='highlight-success'>
                        <MDBox>{Func.numberDisplay(row.gross_profit_percent)}</MDBox>
                        <MDBox>%</MDBox>
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Grid item xs={12} sx={{ mt: 3 }}>
          <Card sx={{ p: 2 }}>
            <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
              <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                {t(`element.show_record`, { num: find.perPage })}&nbsp;
                <Icon>keyboard_arrow_down</Icon>
              </MDButton>
              {renderShow}
              <Pagination
                count={pageTotal}
                variant='outlined'
                color='secondary'
                showFirstButton
                showLastButton
                page={pageNo}
                onChange={(e, p) => view({ ...find, page: p })}
              />
            </MDBox>
          </Card>
        </Grid>
      </>
    );
  };

  return <>{renderList()}</>;
}

QueryProductPrice.propTypes = {
  productCode: PropTypes.string.isRequired,
  setLoading: PropTypes.func.isRequired,
};

export default QueryProductPrice;
